import React from 'react';
import { graphql } from 'gatsby';
import Typography from '../../primitives/typography';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import Container from '../../primitives/grid/container';
import Color, { colors } from '../../primitives/colors';
import { borderRadius, breakpoints } from '../../primitives/tokens';
import Nearest from '../../franchise/nearest';
import Image from '../../primitives/image';
import { FormBuilder } from '../form';

const FranchiseFormHero: React.FC<Queries.FranchiseFormHeroDataFragment> = ({
    title,
    bodyText,
    backgroundImage,
    linkedFormId,
}): JSX.Element => (
    <section css={{
        position: 'relative',
        overflow: 'hidden',
    }}
    >
        <div
            css={{
                position: 'absolute',
                width: '100%',
                height: 'calc(100% - 140px)',
                left: 0,
                top: 0,
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    left: 0,
                    top: 0,
                    backgroundColor: colors.shadesBlack,
                    opacity: 0.6,
                    [breakpoints.mb]: {
                        opacity: 0.8,
                    },
                },
            }}
            aria-hidden="true"
        >
            <Image
                image={backgroundImage}
                css={{
                    width: '100%',
                    height: '100%',
                }}
            />
        </div>
        <Container>
            <Row>
                <Col breakpoints={{
                    dt: { span: 6 },
                }}
                >
                    <div css={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        [breakpoints.mb]: {
                            padding: '24px 0',
                            textAlign: 'center',
                        },
                    }}
                    >
                        <Typography
                            fontSize={{
                                dt: '5xl',
                                mb: '4xl',
                            }}
                            color="shadesWhite"
                            css={{
                                maxWidth: '589px',
                                width: '100%',
                                [breakpoints.mb]: {
                                    maxWidth: 'unset',
                                },
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            fontSize={{
                                dt: '3xl',
                                mb: 'xl',
                            }}
                            color="shadesWhite"
                            css={{
                                marginTop: '8px',
                                maxWidth: '574px',
                                width: '100%',
                                [breakpoints.mb]: {
                                    maxWidth: 'unset',
                                },
                            }}
                            dangerouslySetInnerHTML={{
                                __html: bodyText || '',
                            }}
                        />
                    </div>
                </Col>
                <Col breakpoints={{
                    dt: { span: 6 },
                }}
                >
                    <Color
                        backgroundColor="shadesBlack"
                        css={{
                            borderRadius: borderRadius.medium,
                            padding: '24px 27px',
                            marginTop: '57px',
                            marginBottom: '96px', // 64px + 32px CTA offset
                            // border: `1px solid ${colors.grey400}`,
                            boxShadow: '2px 2px 10px 3px rgba(0, 0, 0, 0.135359)',
                            [breakpoints.mb]: {
                                marginTop: '0',
                                marginBottom: '24px',
                            },
                        }}
                    >
                        <Typography
                            fontSize={{
                                dt: 'xl',
                                tb: 'lg',
                                mb: 'base',
                            }}
                            color="secondaryGreen"
                            css={{
                                maxWidth: '527px',
                                [breakpoints.tb]: {
                                    textAlign: 'center',
                                },
                            }}
                        >
                            Contact us today to schedule your free consultation!
                        </Typography>
                        {linkedFormId && (<FormBuilder formId={linkedFormId} />)}
                    </Color>
                </Col>
            </Row>
        </Container>
        <div css={{
            marginTop: '-32px',
            paddingBottom: '32px',
            position: 'relative',
            [breakpoints.mb]: {
                marginTop: '-12px',
                paddingBottom: '0',
            },
        }}
        >
            <Nearest />
        </div>
    </section>
);

export const query = graphql`
    fragment FranchiseFormHeroData on CsFranchiseFormHero {
        ... on CsFranchiseFormHero {
            title
            bodyText
            linkedFormId
            backgroundImage {
                filename
                title
                description
                url
                contentStackAsset(width: 1440)
            }
        }
    }
`;

export default FranchiseFormHero;
